/**
 * Identidade do usuario: um par Ed25519 (assinatura) e um par X25519
 * (acordo de chaves), gerados no primeiro uso e guardados no SecureStore.
 *
 * Nao ha conta, telefone nem e-mail. A identidade E o par de chaves.
 */
import { concat, fromBase64Url, toBase64Url, wipe } from './bytes.js'
import { InvalidSignatureError } from './errors.js'
import { SIZES, sodium } from './sodium.js'

/** O que se publica: nunca contem chave privada. */
export interface PublicIdentity {
  readonly signPublicKey: Uint8Array
  readonly agreePublicKey: Uint8Array
  readonly fingerprint: Uint8Array
}

export interface IdentitySecrets {
  readonly signPrivateKey: Uint8Array
  readonly agreePrivateKey: Uint8Array
}

/** `fingerprint = BLAKE2b-256(pubEd25519 ‖ pubX25519)` */
export function computeFingerprint(
  signPublicKey: Uint8Array,
  agreePublicKey: Uint8Array,
): Uint8Array {
  return sodium().crypto_generichash(SIZES.fingerprint, concat(signPublicKey, agreePublicKey))
}

export class Identity {
  private constructor(
    readonly publicIdentity: PublicIdentity,
    private readonly secrets: IdentitySecrets,
  ) {}

  static generate(): Identity {
    const s = sodium()
    const sign = s.crypto_sign_keypair()
    const agree = s.crypto_box_keypair()
    return Identity.fromKeys(sign.publicKey, sign.privateKey, agree.publicKey, agree.privateKey)
  }

  /** Reconstroi a identidade a partir do SecureStore. */
  static fromKeys(
    signPublicKey: Uint8Array,
    signPrivateKey: Uint8Array,
    agreePublicKey: Uint8Array,
    agreePrivateKey: Uint8Array,
  ): Identity {
    return new Identity(
      {
        signPublicKey,
        agreePublicKey,
        fingerprint: computeFingerprint(signPublicKey, agreePublicKey),
      },
      { signPrivateKey, agreePrivateKey },
    )
  }

  get fingerprint(): Uint8Array {
    return this.publicIdentity.fingerprint
  }

  /** Fingerprint legivel, para log e para exibir na UI. */
  get fingerprintText(): string {
    return toBase64Url(this.publicIdentity.fingerprint)
  }

  sign(message: Uint8Array): Uint8Array {
    return sodium().crypto_sign_detached(message, this.secrets.signPrivateKey)
  }

  /** Segredo compartilhado X25519 com uma chave publica alheia. */
  agree(theirPublicKey: Uint8Array): Uint8Array {
    return sodium().crypto_scalarmult(this.secrets.agreePrivateKey, theirPublicKey)
  }

  /** Apaga as chaves privadas da memoria. Usado pelo modo coacao. */
  wipe(): void {
    wipe(this.secrets.signPrivateKey, this.secrets.agreePrivateKey)
  }

  /** Somente para persistir no SecureStore — jamais sai do aparelho. */
  exportSecrets(): IdentitySecrets {
    return this.secrets
  }
}

export function verifySignature(
  signature: Uint8Array,
  message: Uint8Array,
  signPublicKey: Uint8Array,
  what = 'mensagem',
): void {
  if (!sodium().crypto_sign_verify_detached(signature, message, signPublicKey)) {
    throw new InvalidSignatureError(what)
  }
}

export function fingerprintFromText(text: string): Uint8Array {
  return fromBase64Url(text)
}
